// The hero's 3D mark: the real vector logo (src/assets/unicorn-logo-traced.svg)
// extruded with a bevel and lit as polished metal, not a flat picture pasted
// on a traced coin. The SVG has two layers: `ink` (cap, head, mane, tassel)
// becomes chrome and `silver` (the two ribbons) becomes brass, at slightly
// different depths so the ribbons sit recessed between the body pieces.
//
// Metal looks like metal only when it has something to reflect, so the scene
// carries a studio environment map and slowly turns it relative to the model:
// highlights sweep across the flat faces instead of sitting still.
//
// `three` (and its loader/environment add-ons) are dynamically imported so they
// code-split into their own chunk — Home.jsx is loaded eagerly for first paint,
// and a static import here would land in the main bundle for every route.

const MAX_YAW = 0.34; // radians of pointer-driven turn
const MAX_PITCH = 0.16;
const SCROLL_ROTATE = 0.32; // extra yaw across a full scroll-through of the hero
const SCROLL_SHIFT = 0.35; // world-unit drift across a full scroll-through
const DAMPING = 0.08;
const MODEL_HEIGHT = 2.4;
// Camera pull-back: the model fills 1 / MODEL_FRAME of its canvas, centred.
// HomeV1Hero shrinks the star targets by the same factor so they land on the
// mark itself, not in the empty margin around it.
export const MODEL_FRAME = 1.22;

// Extrusion depths and bevel, in SVG units (the source art is 958 x 1563).
const INK_DEPTH = 150;
const SILVER_DEPTH = 105;
const BEVEL = 9;

// A dark photo studio to reflect: near-black walls so metal gets real contrast,
// lit by a few bright softboxes. The big gradient card sits behind the camera
// (what a flat front face mirrors), so as the model sways and the environment
// turns, a bright band slides across the chrome instead of one flat grey.
function buildStudio(THREE) {
  const studio = new THREE.Scene();
  studio.background = new THREE.Color(0x1b1f26);

  const bandCanvas = document.createElement('canvas');
  bandCanvas.width = 512;
  bandCanvas.height = 256;
  const g = bandCanvas.getContext('2d');
  const grad = g.createLinearGradient(0, 0, 512, 0);
  grad.addColorStop(0, '#4d5560');
  grad.addColorStop(0.28, '#9aa3ae');
  grad.addColorStop(0.46, '#ffffff');
  grad.addColorStop(0.56, '#ffffff');
  grad.addColorStop(0.74, '#8a929d');
  grad.addColorStop(1, '#3e454f');
  g.fillStyle = grad;
  g.fillRect(0, 0, 512, 256);
  const fade = g.createLinearGradient(0, 0, 0, 256);
  fade.addColorStop(0, 'rgba(0,0,0,0)');
  fade.addColorStop(1, 'rgba(0,0,0,0.4)');
  g.fillStyle = fade;
  g.fillRect(0, 0, 512, 256);
  const bandTexture = new THREE.CanvasTexture(bandCanvas);
  bandTexture.colorSpace = THREE.SRGBColorSpace;

  function panel(w, h, at, tint, power, map) {
    const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(tint).multiplyScalar(power), side: THREE.DoubleSide, map });
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
    m.position.set(...at);
    m.lookAt(0, 0, 0);
    studio.add(m);
  }
  panel(22, 13, [0, 0, 8], 0xffffff, 1.9, bandTexture); // front card, behind the camera
  panel(14, 3, [0, 7, 1], 0xfff4e0, 2.2); // overhead strip
  panel(3, 12, [-7, 0, 2], 0xa8c8ff, 1.8); // cool left strip
  panel(3, 12, [7, 0, 2], 0xffd9a0, 2.2); // warm right strip
  panel(10, 4, [0, -2, -8], 0xffffff, 1.2); // back light for the far bevels
  return studio;
}

export async function createHeroLogoScene({ canvas, svgUrl }) {
  const [THREE, { SVGLoader }, svgText] = await Promise.all([
    import('three'),
    import('three/examples/jsm/loaders/SVGLoader.js'),
    fetch(svgUrl).then((r) => r.text()),
  ]);

  // Pixel ratio still capped (a full retina buffer is the bigger GPU cost —
  // it multiplies every pixel this renders every frame), but antialias is
  // back on: off, the mark's edges and the thin gaps between pieces (the
  // ears, the ribbon seams) read soft/aliased at this size, which is what
  // "sharper" was asking to fix. MSAA only costs extra at edges, not the
  // whole frame, so it's a small price for a real difference here.
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);

  const pmrem = new THREE.PMREMGenerator(renderer);
  const studio = buildStudio(THREE);
  const envTexture = pmrem.fromScene(studio, 0.03).texture;
  scene.environment = envTexture;
  // The studio only exists to be baked into the env map; free it right away.
  studio.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    if (o.material) {
      if (o.material.map) o.material.map.dispose();
      o.material.dispose();
    }
  });

  // ----- vector -> extruded solids -----
  const svg = new SVGLoader().parse(svgText);
  const viewBox = svg.xml.getAttribute('viewBox').split(/\s+/).map(Number);
  const [, , vbW, vbH] = viewBox;
  const s = MODEL_HEIGHT / vbH;

  // Standard rather than Physical: no clearcoat layer, so the shader does one
  // shading pass per light/pixel instead of two. Not a visible difference on
  // an already-glossy metal at this size, and cheaper every frame.
  const chrome = new THREE.MeshStandardMaterial({
    color: 0xcfd4db,
    metalness: 1,
    roughness: 0.14,
    envMapIntensity: 1.0,
  });
  const brass = new THREE.MeshStandardMaterial({
    color: 0xc7a252,
    metalness: 1,
    roughness: 0.22,
    envMapIntensity: 1.35,
  });

  const geometries = [];
  const model = new THREE.Group();
  // SVG's y axis points down; a negative y scale flips it (three flips face
  // winding for negative-determinant transforms, so the faces stay outward).
  model.scale.set(s, -s, s);

  function addLayer(path, depth, material) {
    const shapes = SVGLoader.createShapes(path);
    const geo = new THREE.ExtrudeGeometry(shapes, {
      depth,
      bevelEnabled: true,
      bevelThickness: BEVEL * 2,
      bevelSize: BEVEL,
      bevelOffset: -BEVEL, // keep the silhouette (and the thin gaps between pieces) as drawn
      // Segment counts trade smoothness for triangle count; building the
      // geometry finer is a one-time cost (not a per-frame one) and the
      // curves — the neck, the ear points, the ribbon edges — are exactly
      // where a coarser mesh reads as faceted rather than sharp.
      bevelSegments: 5,
      curveSegments: 10,
    });
    geo.translate(-vbW / 2, -vbH / 2, -depth / 2);
    geometries.push(geo);
    model.add(new THREE.Mesh(geo, material));
  }
  // Document order in the SVG: ink first, ribbons second.
  addLayer(svg.paths[0], INK_DEPTH, chrome);
  addLayer(svg.paths[1], SILVER_DEPTH, brass);

  const pivot = new THREE.Group();
  pivot.add(model);
  scene.add(pivot);

  // A little direct light on top of the environment: gives the bevels a crisp
  // warm edge highlight and a cool rim from behind.
  const key = new THREE.DirectionalLight(0xfff0d0, 0.6);
  key.position.set(2.5, 3, 4);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x9cc4ff, 0.4);
  rim.position.set(-3, 1, -2);
  scene.add(rim);

  const fovRad = (camera.fov * Math.PI) / 180;
  camera.position.z = (MODEL_HEIGHT / (2 * Math.tan(fovRad / 2))) * MODEL_FRAME;

  const target = { x: 0, y: 0 };
  const current = { x: 0, y: 0 };
  let scrollT = 0;
  let raf = null;
  let disposed = false;
  let running = true; // paused via setVisible while scrolled off screen or the tab is hidden

  function resize() {
    const parent = canvas.parentElement;
    if (!parent) return;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  const ro = new ResizeObserver(resize);
  if (canvas.parentElement) ro.observe(canvas.parentElement);
  resize();

  function frame(now) {
    if (disposed || !running) return;
    const t = now * 0.001;
    current.x += (target.x - current.x) * DAMPING;
    current.y += (target.y - current.y) * DAMPING;
    // Idle: a slow sway (not a full spin, which would show the mirrored back)
    // plus a gentle float; pointer and scroll add on top.
    pivot.rotation.y = current.x * MAX_YAW + Math.sin(t * 0.45) * 0.26 + scrollT * SCROLL_ROTATE;
    pivot.rotation.x = current.y * MAX_PITCH + Math.sin(t * 0.33 + 1) * 0.045;
    pivot.position.y = Math.sin(t * 0.8) * 0.04;
    pivot.position.x = -scrollT * SCROLL_SHIFT;
    // Swing the studio back and forth (a full turn would carry the front
    // card out of view and leave the chrome reflecting black walls).
    scene.environmentRotation.y = Math.sin(t * 0.35) * 0.55;
    renderer.render(scene, camera);
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  return {
    setPointer(nx, ny) {
      target.x = nx;
      target.y = ny;
    },
    setScroll(v) {
      scrollT = v;
    },
    // Stops the render loop entirely rather than just skipping the visual
    // update — a canvas nobody can see (off screen, or the tab backgrounded)
    // costs nothing while paused instead of rendering wasted frames.
    setVisible(intersecting, tabVisible = true) {
      const shouldRun = intersecting !== false && tabVisible !== false;
      if (shouldRun === running || disposed) {
        running = shouldRun;
        return;
      }
      running = shouldRun;
      if (running && !raf) raf = requestAnimationFrame(frame);
      else if (!running && raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    },
    dispose() {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      geometries.forEach((g) => g.dispose());
      chrome.dispose();
      brass.dispose();
      envTexture.dispose();
      pmrem.dispose();
      renderer.dispose();
    },
  };
}
